import { useState } from 'react'
import { CheckCheck, MessageSquare } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useSaveAlertReview } from '@/hooks/useAlertReviews'
import { useToast } from '@/hooks/use-toast'
import type { ReviewedAlert } from '@/lib/alertReviews'

function formatTime(timestamp: number) {
  return new Date(timestamp * 1000).toLocaleString()
}

export function AlertReviewCard({ alert, initiallyEditing = false }: { alert: ReviewedAlert; initiallyEditing?: boolean }) {
  const [editing, setEditing] = useState(initiallyEditing)
  const [note, setNote] = useState('')
  const [acknowledged, setAcknowledged] = useState(true)
  const save = useSaveAlertReview()
  const { toast } = useToast()
  const missing = !alert.acknowledged && !alert.has_note ? 'Needs acknowledgement and comment'
    : !alert.acknowledged ? 'Needs acknowledgement' : 'Needs comment'

  async function submit() {
    try {
      await save.mutateAsync({ id: alert.id, note: note.trim(), acknowledged })
      setNote('')
      setEditing(false)
      toast({ title: 'Review saved', description: `${alert.symbol} · ${note.trim() ? 'Comment saved' : 'Marked checked'}` })
    } catch { /* The form keeps its content and displays the request error. */ }
  }

  return <article className="rounded-lg border bg-card p-4" aria-label={`${alert.symbol} ${alert.kind} alert`}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{alert.kind.replace(/_/g, ' ')}</span>
          <Badge variant={alert.outstanding ? 'secondary' : 'success'}>
            {alert.outstanding ? missing : 'Reviewed'}
          </Badge>
          {alert.owner_username && <Badge variant="outline">Personal</Badge>}
        </div>
        <p className="text-xs text-muted-foreground">{formatTime(alert.occurred_at)}</p>
      </div>
      <Button variant="outline" size="sm" onClick={() => { save.reset(); setEditing(!editing) }} disabled={save.isPending}>
        <MessageSquare className="mr-2 h-4 w-4" />{editing ? 'Close review' : 'Review'}
      </Button>
    </div>
    <p className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap break-words text-sm">{alert.text}</p>
    {alert.acknowledgements.length > 0 && <div className="mt-3 space-y-1 border-t pt-3">
      {alert.acknowledgements.map((ack, index) => <p key={`${ack.username}-${index}`} className="flex items-start gap-2 text-xs text-muted-foreground">
        <CheckCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
        <span><span className="font-medium text-foreground">{ack.username}</span> checked · {formatTime(ack.created_at)}</span>
      </p>)}
    </div>}
    {alert.notes.length > 0 && <ol className="mt-3 space-y-2" aria-label="Review comments">
      {alert.notes.map((comment) => <li key={comment.id} className="rounded-md bg-muted/50 p-3">
        <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">{comment.username}</span> · {formatTime(comment.created_at)}</p>
        <p className="mt-1 whitespace-pre-wrap break-words text-sm">{comment.note}</p>
      </li>)}
    </ol>}
    {editing && <form className="mt-4 space-y-3 border-t pt-4" onSubmit={(event) => { event.preventDefault(); void submit() }}>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={acknowledged} onCheckedChange={setAcknowledged} disabled={save.isPending} />
        I checked this alert
      </label>
      <label className="block space-y-2 text-sm">
        <span>Comment</span>
        <textarea rows={3} maxLength={4000} value={note} onChange={(event) => setNote(event.target.value)} disabled={save.isPending}
          placeholder="What did you check or decide?"
          className="w-full resize-y rounded-md border border-input bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
      </label>
      <p className="text-xs text-muted-foreground">Add a comment to explain what triggered this alert and whether its settings need changing. Comments are saved with your name and time.</p>
      {save.error && <p role="alert" className="text-sm text-destructive">{save.error instanceof Error ? save.error.message : 'Could not save the review. Please try again.'}</p>}
      <Button type="submit" size="sm" disabled={save.isPending || (!acknowledged && !note.trim())}>
        {save.isPending ? 'Saving…' : 'Save review'}
      </Button>
    </form>}
  </article>
}
